import { useState, useCallback, useEffect } from 'react';
import { FilterFieldName, Filters, FilterValues } from './FilterBarContext';
import { labelService } from '@/modules/labels/api/label.service';
import { LabelValueOption } from '@/modules/labels/components/LabelValuesSelect';
import { LocationModel } from '@/modules/locations/types/LocationModel';
import { AssetModel } from '@/modules/assets/types/AssetModel';
import { AssetTypeModel } from '@/modules/asset-types/types/AssetTypeModel';
import { locationService } from '@/modules/locations/api/locations/location.service';
import { assetService } from '@/modules/assets/api/assets/asset.service';
import { assetTypeService } from '@/modules/asset-types/api/asset-types/asset-type.service';
import { TransactionType, TransactionStatus, TransactionSource } from '@/modules/transactions/api/transactions/transaction.contracts';
import { BatteryStatusSearchParam } from '@/modules/trackers/api/trackers/tracker.contracts';
import { useTranslation } from '@/lib';
import { Filter } from '@mui/icons-material';

const useFilterBar = ({
  onSearch,
  initialFilterValues = {},
}: { onSearch?: (filterValues: FilterValues) => void; initialFilterValues?: FilterValues } = {}) => {
  const { t } = useTranslation();
  const [labelValues, setLabelValues] = useState<LabelValueOption[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetTypeModel[] | undefined>(undefined);
  const [location, setLocation] = useState<LocationModel | undefined>(undefined);
  const [asset, setAsset] = useState<AssetModel | undefined>(undefined);
  const [toLocation, setToLocation] = useState<LocationModel | undefined>(undefined);
  const [fromLocation, setFromLocation] = useState<LocationModel | undefined>(undefined);

  const [filtersToFetch, setFiltersToFetch] = useState<FilterFieldName[]>([]);
  const filtersRequireFetching = [
    FilterFieldName.labelValueIds,
    FilterFieldName.assetTypeIds,
    FilterFieldName.assetId,
    FilterFieldName.locationId,
    FilterFieldName.assetTypeId,
    FilterFieldName.toLocationId,
    FilterFieldName.fromLocationId,
  ];

  useEffect(() => {
    // Check if any of the initial filter values require fetching
    const filters: FilterFieldName[] = [];
    Object.keys(initialFilterValues).forEach((key) => {
      if (filtersRequireFetching.includes(key as FilterFieldName)) {
        filters.push(key as FilterFieldName);
      }
    });
    if (filters.length > 0) {
      setFiltersToFetch(filters);
    }
  }, [initialFilterValues]);

  useEffect(() => {
    if (filtersToFetch.includes(FilterFieldName.labelValueIds)) {
      const fetchLabelValues = async () => {
        const labels = await labelService.getAll();
        if (labels.isSuccess) {
          const labelValues = labels.payload.flatMap((label) =>
            label.values.map((labelValue) => ({
              labelName: label.dto.name,
              labelId: label.dto.id,
              labelValueId: labelValue.dto.id,
              labelValueName: labelValue.dto.value,
            })),
          );

          const selectedLabelValues = labelValues.filter(
            (lv) => initialFilterValues[FilterFieldName.labelValueIds]?.includes(lv.labelValueId),
          );
          setLabelValues(selectedLabelValues);
        }
      };
      fetchLabelValues();
    }

    if (filtersToFetch.includes(FilterFieldName.locationId)) {
      const fetchLocation = async () => {
        const locationId = initialFilterValues[FilterFieldName.locationId] as number;
        if (Number.isFinite(locationId)) {
          const location = await locationService.getById(locationId);
          if (location.isSuccess) {
            setLocation(location.payload);
          }
        }
      };
      fetchLocation();
    }

    if (filtersToFetch.includes(FilterFieldName.assetId)) {
      const fetchAsset = async () => {
        const assetId = initialFilterValues[FilterFieldName.assetId] as number;
        if (Number.isFinite(assetId)) {
          const asset = await assetService.getById(assetId);
          if (asset.isSuccess) {
            setAsset(asset.payload);
          }
        }
      };
      fetchAsset();
    }

    if (filtersToFetch.includes(FilterFieldName.assetTypeIds)) {
      const fetchAssetTypes = async () => {
        const ids = initialFilterValues[FilterFieldName.assetTypeIds] as number[];
        const assetTypes = await assetTypeService.getAll();
        if (assetTypes.isSuccess) {
          const filteredAssetTypes = assetTypes.payload.filter((assetType) => ids.includes(assetType.dto.id));
          setAssetTypes(filteredAssetTypes);
        }
      };
      fetchAssetTypes();
    }
    if (filtersToFetch.includes(FilterFieldName.assetTypeId)) {
      const fetchAssetType = async () => {
        const assetTypeId = initialFilterValues[FilterFieldName.assetTypeId] as number;
        if (Number.isFinite(assetTypeId)) {
          const assetType = await assetTypeService.getById(assetTypeId);
          if (assetType.isSuccess) {
            setAssetTypes([assetType.payload]);
          }
        }
      };
      fetchAssetType();
    }

    if (filtersToFetch.includes(FilterFieldName.toLocationId)) {
      const fetchToLocation = async () => {
        const toLocationId = initialFilterValues[FilterFieldName.toLocationId] as number;
        if (Number.isFinite(toLocationId)) {
          const location = await locationService.getById(toLocationId);
          if (location.isSuccess) {
            setToLocation(location.payload);
          }
        }
      };
      fetchToLocation();
    }

    if (filtersToFetch.includes(FilterFieldName.fromLocationId)) {
      const fetchFromLocation = async () => {
        const fromLocationId = initialFilterValues[FilterFieldName.fromLocationId] as number;
        if (Number.isFinite(fromLocationId)) {
          const location = await locationService.getById(fromLocationId);
          if (location.isSuccess) {
            setFromLocation(location.payload);
          }
        }
      };
      fetchFromLocation();
    }
  }, [filtersToFetch]);

  const getActiveFilters = (filterValues: FilterValues): Filters => {
    const activeFilters: Filters = {};
    for (const key of Object.keys(filterValues)) {
      switch (key) {
        // Dayjs
        case FilterFieldName.dateFrom:
        case FilterFieldName.dateTo:
        case FilterFieldName.arrivedAtDateFrom:
        case FilterFieldName.arrivedAtDateTo:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.format('YYYY-MM-DD') ?? '',
            };
          }
          break;
        // Arrays
        case FilterFieldName.labelValueIds:
          if (filterValues[key]) {
            const humanValues = labelValues.map((option) => `${option.labelName}:${option.labelValueName}`).join(', ');
            const humanValue = humanValues.length > 50 ? `${humanValues.substring(0, 50)}...` : humanValues;
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: humanValue,
            };
          }
          break;
        case FilterFieldName.assetTypeIds:
          if (filterValues[key]) {
            const humanValues = assetTypes?.map((type) => type.name).join(', ') ?? '';
            const humanValue = humanValues.length > 50 ? `${humanValues.substring(0, 50)}...` : humanValues;
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: humanValue,
            };
          }
          break;
        // Numbers
        case FilterFieldName.assetId:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: asset?.dto.code ?? '',
            };
          }
          break;
        case FilterFieldName.locationId:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: location?.dto.name ?? '',
            };
          }
          break;
        case FilterFieldName.assetTypeId:
          if (filterValues[key]) {
            if (assetTypes && assetTypes.length > 0) {
              activeFilters[key] = {
                value: filterValues[key],
                humanValue: assetTypes[0].dto.name,
              };
            } else {
              activeFilters[key] = {
                value: filterValues[key],
                humanValue: '',
              };
            }
          }
          break;
        case FilterFieldName.searchNumber:
        case FilterFieldName.stayTimeInDays:
        case FilterFieldName.pageNumber:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.toLocationId:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: toLocation?.dto.name ?? '',
            };
          }
          break;
        case FilterFieldName.fromLocationId:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: fromLocation?.dto.name ?? '',
            };
          }
          break;
        // Enums
        case FilterFieldName.eventType:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.logCategory:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.logSeverity:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.trackerType:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.linkedTracker:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.batteryStatus:
          if (filterValues[key]) {
            const batteryStatus = filterValues[key] as BatteryStatusSearchParam;
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: t(batteryStatus.toLowerCase()),
            };
          }
          break;
        case FilterFieldName.transactionType:
          if (filterValues[key]) {
            const transactionType = filterValues[key] as TransactionType;
            const humanValue = t(transactionType.toLowerCase());
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: humanValue,
            };
          }
          break;
        case FilterFieldName.transactionStatus:
          if (filterValues[key]) {
            const transactionStatus = filterValues[key] as TransactionStatus;
            const humanValue = t(transactionStatus.toLowerCase());
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: humanValue,
            };
          }
          break;
        case FilterFieldName.transactionSource:
          if (filterValues[key]) {
            const transactionSource = filterValues[key] as TransactionSource;
            const humanValue = t(transactionSource.toLowerCase());
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: humanValue,
            };
          }
          break;
        case FilterFieldName.sortDirection:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.sortOption:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;
        case FilterFieldName.movementType:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key]?.toString() ?? '',
            };
          }
          break;

        // String
        case FilterFieldName.searchText:
        case FilterFieldName.messageId:
          if (filterValues[key]) {
            activeFilters[key] = {
              value: filterValues[key],
              humanValue: filterValues[key] ?? '',
            };
          }
          break;

        // Default case
        default:
          throw Error(`Unknown filter field: ${key}`);
      }
    }
    return activeFilters;
  };

  const initialFilters = getActiveFilters(initialFilterValues);

  const [activeFilters, setActiveFilters] = useState<Filters>(initialFilters);

  useEffect(() => {
    const filterValues = getFilterValues();
    setActiveFilters(getActiveFilters(filterValues));
  }, [labelValues, assetTypes, location, asset, toLocation, fromLocation]);

  const setFilterValue = (field: FilterFieldName, value: unknown, humanValue?: string) => {
    setActiveFilters((prevValues) => {
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        const { [field]: _, ...rest } = prevValues;

        return rest;
      } else {
        // If the value is not undefined, null or empty string, we set it
        return {
          ...prevValues,
          [field]: { value: value, humanValue: humanValue },
        };
      }
    });
  };

  const getFilterValue = (field: FilterFieldName) => {
    const filterValue = activeFilters[field]?.value;
    if (filterValue === undefined || filterValue === null || filterValue === '') {
      return undefined;
    }
    return filterValue;
  };
  const getFilterHumanValue = (field: FilterFieldName) => {
    const filterValue = activeFilters[field]?.humanValue;
    if (filterValue === undefined || filterValue === null || filterValue === '') {
      return undefined;
    }
    return filterValue;
  };

  const getFilterValues = () => {
    const filterValues: FilterValues = {};
    Object.keys(activeFilters).forEach((key) => {
      switch (key) {
        // Dayjs
        case FilterFieldName.dateFrom:
        case FilterFieldName.dateTo:
        case FilterFieldName.arrivedAtDateFrom:
        case FilterFieldName.arrivedAtDateTo:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        // Number Array
        case FilterFieldName.labelValueIds:
        case FilterFieldName.assetTypeIds:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        // Number
        case FilterFieldName.assetId:
        case FilterFieldName.locationId:
        case FilterFieldName.assetTypeId:
        case FilterFieldName.searchNumber:
        case FilterFieldName.stayTimeInDays:
        case FilterFieldName.pageNumber:
        case FilterFieldName.toLocationId:
        case FilterFieldName.fromLocationId:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        //Enum
        case FilterFieldName.eventType:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.logCategory:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.logSeverity:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.trackerType:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.linkedTracker:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.batteryStatus:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.transactionType:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.transactionStatus:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.transactionSource:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.sortDirection:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.sortOption:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        case FilterFieldName.movementType:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;
        // String
        case FilterFieldName.searchText:
        case FilterFieldName.messageId:
          if (activeFilters[key]?.value) {
            filterValues[key] = activeFilters[key]!.value;
          }
          break;

        default:
          throw Error(`Unknown filter field: ${key}`);
      }
    });
    return filterValues;
  };

  const triggerSearch = useCallback(() => {
    if (onSearch) {
      onSearch(getFilterValues());
    }
  }, [activeFilters, onSearch]);

  const isFilterActive = (filterName: FilterFieldName) => {
    const value = activeFilters[filterName]?.value;

    return !(value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0));
  };

  return { filterValues: getFilterValues(), setFilterValue, triggerSearch, isFilterActive, getFilterValue, getFilterHumanValue };
};

export default useFilterBar;
